import React, { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Drawer, Input, Space, Radio, Checkbox, Tag, Button, Empty, message, Select } from 'antd';

interface IndexProps {
  ref: React.Ref<unknown>;
  followups: API.Word[];
  approaches: API.Word[];
  setFollowups: (values: API.Word[]) => void;
  setApproaches: (values: API.Word[]) => void;
  workerVersions: API.SelectOption[];
}

const TextMap = [
  ["笔记标题", "source",],
  ["招呼语", "greeting",],
  ["引导语1", "followup"],
  ["二维码", "qrcode"],
]

type TextType = "followup" | "greeting" | "source" | "qrcode";

const Index: React.FC<IndexProps> = forwardRef(({ followups, approaches, setFollowups, setApproaches, workerVersions }, ref) => {
  const [visible, setVisible] = useState<boolean>(false);

  const [currentType, setCurrentType] = useState<TextType>("followup");

  const [keyText, setKeyText] = useState<string>("");
  const [replaceText, setReplaceText] = useState<string>("");

  const [selectedFollowup, setSelectedFollowup] = useState<string[]>([]);
  const [selectedApproach, setSelectedApproach] = useState<string[]>([]);

  useImperativeHandle(ref, () => {
    return {
      setVisible: (value: boolean) => setVisible(value),
    }
  })

  const workerMap: Map<string, API.SelectOption> = useMemo(() => {
    const map = new Map()
    workerVersions.forEach(w => map.set(w.value, w))
    return map
  }, [workerVersions])

  const handle = (values: API.Word[]) => {
    return values.filter(vo => vo[currentType] && vo[currentType].includes(keyText))
  }

  const searchResult = useMemo(() => {
    if (!keyText) return { followups: [], approaches: [] }
    return {
      followups: handle(followups),
      approaches: handle(approaches),
    }
  }, [keyText, currentType, followups, approaches])

  const getReplaceData = (data: API.Word[], selectedData: string[]) => {
    return data.map(f => {
      if (selectedData.includes(f.id)) {
        return {
          ...f,
          [currentType]: f[currentType].replace(keyText, replaceText),
        }
      }
      return f
    })
  }

  const handleReplace = () => {
    console.log("replace", selectedApproach, selectedFollowup);
    console.log("replaceText", replaceText);

    if (selectedApproach.length === 0 && selectedFollowup.length === 0) {
      return message.warning("请选择需要替换的项！")
    }

    if (selectedFollowup.length > 0) {
      setFollowups(getReplaceData(followups, selectedFollowup))
    }

    if (selectedApproach.length > 0) {
      setApproaches(getReplaceData(approaches, selectedApproach))
    }

    message.success("操作成功！")
    setSelectedApproach([])
    setSelectedFollowup([])
  }

  return <Drawer
    title="批量替换文案/二维码"
    visible={visible}
    placement="right"
    width="700"
    onClose={() => setVisible(false)}
  >
    <Space direction="vertical" style={{ width: '100%' }}>
      <h3>相关文字</h3>
      <Radio.Group value={currentType} onChange={e => {
        const value = e.target.value
        if (value === currentType) return
        if (value === 'qrcode' || currentType === 'qrcode') {
          setKeyText("")
          setReplaceText("")
        }
        setCurrentType(e.target.value)
        setSelectedApproach([])
        setSelectedFollowup([])
      }}>
        {
          TextMap.map(vo => <Radio.Button key={vo[1]} value={vo[1]}>{vo[0]}</Radio.Button>)
        }
      </Radio.Group>
      {
        currentType !== 'qrcode' ?
          <Input placeholder="请输入被替换的文字" value={keyText} onChange={e => setKeyText(e.target.value.trim())} allowClear /> :
          <Select placeholder="请选择被替换的二维码" style={{ width: '100%' }} options={workerVersions} value={keyText || null} onChange={e => setKeyText(e)} />
      }

      <h3>替换为</h3>
      <Input.Group compact>
        {
          currentType === 'qrcode' ? <Select
            placeholder="请选择替换为的二维码"
            style={{ width: 'calc(100% - 100px)' }}
            value={replaceText || null}
            onChange={e => setReplaceText(e)}
            options={workerVersions}
          /> : <Input
            style={{ width: 'calc(100% - 100px)' }}
            placeholder="请填入替换为的文字"
            value={replaceText}
            allowClear
            onChange={e => setReplaceText(e.target.value.trim())}
          />
        }
        <Button style={{ width: 100 }} type="primary" onClick={handleReplace}>确定</Button>
      </Input.Group>
      <h3>内容出现位置</h3>
      <h4>来源配置</h4>
      <Checkbox.Group style={{ width: '100%' }} value={selectedFollowup} onChange={e => setSelectedFollowup(e as string[])}>
        {
          searchResult.followups.length > 0 ? searchResult.followups.map(vo => <CheckboxComponent key={vo.id} workerMap={workerMap} vo={vo} currentType={currentType} keyText={keyText} replaceText={replaceText} />) : <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}><Empty /></div>
        }
      </Checkbox.Group>
      <h4>主动触发配置</h4>
      <Checkbox.Group style={{ width: '100%' }} value={selectedApproach} onChange={e => setSelectedApproach(e as string[])}>
        {
          searchResult.approaches.length > 0 ? searchResult.approaches.map(vo => <CheckboxComponent key={vo.id} workerMap={workerMap} vo={vo} currentType={currentType} keyText={keyText} replaceText={replaceText} />) : <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}><Empty /></div>
        }
      </Checkbox.Group>
    </Space>
  </Drawer>
})

export default Index;

const CheckboxItem: React.FC<{ vo: API.Word; currentType: TextType; key: React.Key; workerMap: Map<string, API.SelectOption>; keyText: string; replaceText: string; }> = ({ vo, currentType, workerMap, keyText, replaceText }) => {
  const value = vo[currentType]

  let textArr: any = value.split(keyText)
  if (currentType !== 'qrcode') {
    const keyTextIndexInValue = value.indexOf(keyText)
    const text = <span style={{ color: '#F11A7B', textDecoration: 'line-through' }}>{keyText}</span>
    const rText = <span style={{ color: '#1890FF' }}>{replaceText}</span>
    if (keyTextIndexInValue === 0) textArr = [text, rText, ...textArr]
    if (keyTextIndexInValue > 0) {
      textArr.splice(1, 0, text)
      textArr.splice(2, 0, rText)
    }
  }

  return <Checkbox style={{ margin: 0, paddingBottom: 6, marginBottom: 6, borderBottom: '1px solid #f0f0f0' }} value={vo.id}>
    <Space direction="vertical">
      <Tag>{vo.source}</Tag>
      <div>{currentType !== 'qrcode' ? textArr : workerMap.get(value)?.label}</div>
    </Space>
  </Checkbox>
}

const CheckboxComponent = React.memo(CheckboxItem)